import React, { Component } from 'react';
import { BookOpen, Home, User, Search, Bell, Settings, MessageSquare, FileText, Calendar, CheckSquare, Video, Cpu, Clock, Shield, Send, ChevronRight, Star, Filter, MoreHorizontal } from 'lucide-react';
// Color Palette Constants
const COLORS = {
  gold: '#C9A962',
  goldDark: '#B89650',
  brown: '#8B6914',
  cream: '#FDF8F3',
  beige: '#F5F0E6',
  deepBrown: '#5D4E37',
  dark: '#3D3428',
  white: '#FFFFFF',
  textPrimary: '#3D3428',
  textSecondary: '#8B6914'
};
// Helper for layout wrapper
const ScreenWrapper = ({
  children,
  className = '',
  hideBottomNav = false,
  activeTab = 'home'
}: {
  children: React.ReactNode;
  className?: string;
  hideBottomNav?: boolean;
  activeTab?: string;
}) => <div className={`w-full h-full bg-[#FDF8F3] overflow-hidden flex flex-col font-sans relative ${className}`} data-id="element-2">
    {children}
    {!hideBottomNav && <BottomNav active={activeTab} data-id="element-3" />}
  </div>;
// Shared Bottom Navigation Component
const BottomNav = ({
  active
}: {
  active: string;
}) => <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-[#F5F0E6] px-6 py-4 flex justify-between items-center z-50 pb-8" data-id="element-4">
    {[{
    id: 'notes',
    icon: FileText,
    label: 'Notes'
  }, {
    id: 'chats',
    icon: MessageSquare,
    label: 'Chats'
  }, {
    id: 'home',
    icon: Home,
    label: 'Home'
  }, {
    id: 'settings',
    icon: Settings,
    label: 'Settings'
  }].map(item => <div key={item.id} className={`flex flex-col items-center gap-1 ${active === item.id ? 'text-[#C9A962]' : 'text-gray-300'}`} data-id="element-5">
        <item.icon className={`w-6 h-6 ${active === item.id ? 'fill-current' : ''}`} strokeWidth={active === item.id ? 2.5 : 2} data-id="element-6" />
        <span className="text-[10px] font-medium" data-id="element-7">{item.label}</span>
      </div>)}
  </div>;
// SCREEN 1: HOME
export const HomeScreen = () => <ScreenWrapper activeTab="home" data-id="element-8">
    {/* Header */}
    <div className="pt-14 pb-6 px-6 text-center" data-id="element-9">
      <h1 className="text-2xl font-bold text-[#5D4E37] tracking-wide" data-id="element-10">
        INJAZ NAJAH
      </h1>
    </div>

    {/* Menu Cards */}
    <div className="px-6 flex-1 overflow-y-auto pb-24 space-y-4 no-scrollbar" data-id="element-11">
      {[{
      title: 'Notes Marketplace',
      subtitle: 'Buy & Sell Notes',
      icon: FileText,
      color: 'bg-orange-50 text-orange-600'
    }, {
      title: 'Tutor Booking',
      subtitle: 'Find Expert Tutors',
      icon: Calendar,
      color: 'bg-blue-50 text-blue-600'
    }, {
      title: 'Smart Task Manager',
      subtitle: 'Focus Mode Included',
      icon: CheckSquare,
      color: 'bg-green-50 text-green-600'
    }, {
      title: 'Live Classes',
      subtitle: 'Subscribe to Courses',
      icon: Video,
      color: 'bg-red-50 text-red-600'
    }, {
      title: 'AI Learning System',
      subtitle: 'Smart Study Assistant',
      icon: Cpu,
      color: 'bg-purple-50 text-purple-600'
    }].map((item, i) => <div key={i} className="bg-white p-5 rounded-3xl shadow-sm border border-[#F5F0E6] flex items-center gap-4 hover:shadow-md transition-all" data-id="element-12">
          <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center shadow-sm`} data-id="element-13">
            <item.icon className="w-7 h-7" data-id="element-14" />
          </div>
          <div className="flex-1" data-id="element-15">
            <h3 className="font-bold text-[#3D3428] text-lg" data-id="element-16">{item.title}</h3>
            <p className="text-[#8B6914] text-sm opacity-80" data-id="element-17">{item.subtitle}</p>
          </div>
          <div className="w-8 h-8 rounded-full bg-[#FDF8F3] flex items-center justify-center text-[#C9A962]" data-id="element-18">
            <ChevronRight className="w-5 h-5" data-id="element-19" />
          </div>
        </div>)}
    </div>
  </ScreenWrapper>;
// SCREEN 2: NOTES MARKETPLACE
export const NotesMarketplaceScreen = () => <ScreenWrapper activeTab="notes" data-id="element-20">
    <div className="pt-14 px-6 pb-4 bg-white rounded-b-3xl shadow-sm z-10" data-id="element-21">
      <div className="flex justify-between items-center mb-4" data-id="element-22">
        <h2 className="text-2xl font-bold text-[#3D3428]" data-id="element-23">Notes Marketplace</h2>
        <Filter className="w-5 h-5 text-[#C9A962]" data-id="element-24" />
      </div>

      {/* Search Bar */}
      <div className="relative mb-4" data-id="element-25">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-26" />
        <input type="text" value="Advanced Math Grade 11" readOnly className="w-full bg-[#FDF8F3] border border-[#F5F0E6] rounded-xl py-3 pl-10 pr-4 text-sm text-[#3D3428] font-medium focus:outline-none focus:border-[#C9A962]" data-id="element-27" />
      </div>

      {/* Filter Tag */}
      <div className="flex gap-2" data-id="element-28">
        <span className="px-3 py-1.5 bg-[#5D4E37] text-white text-xs rounded-lg font-medium" data-id="element-29">
          Best Rated First
        </span>
        <span className="px-3 py-1.5 bg-[#FDF8F3] text-[#8B6914] text-xs rounded-lg font-medium border border-[#F5F0E6]" data-id="element-30">
          Price: Low to High
        </span>
      </div>
    </div>

    {/* Listings */}
    <div className="px-6 py-6 space-y-4 overflow-y-auto pb-24" data-id="element-31">
      {[{
      title: 'Advanced Math Grade 11',
      subtitle: 'Algebra',
      price: '3 OMR',
      rating: 4.8
    }, {
      title: 'Trigonometry & Calculus',
      subtitle: 'Complete Notes',
      price: '4 OMR',
      rating: 4.9
    }, {
      title: 'Grade 11 Math Guide',
      subtitle: 'Full Syllabus',
      price: '5 OMR',
      rating: 4.7
    }].map((item, i) => <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-[#F5F0E6] flex flex-col gap-3" data-id="element-32">
          <div className="flex justify-between items-start" data-id="element-33">
            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center text-orange-500" data-id="element-34">
              <FileText className="w-6 h-6" data-id="element-35" />
            </div>
            <div className="flex items-center gap-1 bg-[#FDF8F3] px-2 py-1 rounded-lg" data-id="element-36">
              <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" data-id="element-37" />
              <span className="text-xs font-bold text-[#3D3428]" data-id="element-38">
                {item.rating}
              </span>
            </div>
          </div>

          <div data-id="element-39">
            <h3 className="font-bold text-[#3D3428] text-lg leading-tight mb-1" data-id="element-40">
              {item.title}
            </h3>
            <p className="text-gray-500 text-sm" data-id="element-41">{item.subtitle}</p>
          </div>

          <div className="flex items-center justify-between mt-2 pt-3 border-t border-gray-50" data-id="element-42">
            <span className="font-bold text-[#5D4E37] text-lg" data-id="element-43">
              {item.price}
            </span>
            <button className="bg-[#C9A962] text-white px-4 py-2 rounded-xl text-sm font-bold shadow-md shadow-[#C9A962]/20" data-id="element-44">
              View Notes
            </button>
          </div>
        </div>)}
    </div>
  </ScreenWrapper>;
// SCREEN 3: TUTOR BOOKING
export const TutorBookingScreen = () => <ScreenWrapper activeTab="home" data-id="element-45">
    <div className="pt-14 px-6 pb-2" data-id="element-46">
      <h2 className="text-2xl font-bold text-[#3D3428] mb-4" data-id="element-47">Tutor Booking</h2>

      {/* Search */}
      <div className="relative mb-6" data-id="element-48">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-49" />
        <input type="text" placeholder="Find a tutor for Grade 11" className="w-full bg-white border border-[#F5F0E6] rounded-xl py-3 pl-10 pr-4 text-sm text-[#3D3428] focus:outline-none shadow-sm" data-id="element-50" />
      </div>
    </div>

    {/* Tutors List */}
    <div className="px-6 space-y-4 overflow-y-auto pb-24" data-id="element-51">
      {[{
      name: 'Aisha Al-Harthy',
      subject: 'Advanced Math',
      price: '6 OMR',
      initials: 'AA',
      color: 'bg-purple-100 text-purple-600'
    }, {
      name: 'Khalid Al-Siyabi',
      subject: 'Physics',
      price: '5 OMR',
      initials: 'KA',
      color: 'bg-blue-100 text-blue-600'
    }, {
      name: 'Fatma Al-Balushi',
      subject: 'Chemistry',
      price: '5.5 OMR',
      initials: 'FA',
      color: 'bg-green-100 text-green-600'
    }].map((tutor, i) => <div key={i} className="bg-white p-5 rounded-3xl shadow-sm border border-[#F5F0E6]" data-id="element-52">
          <div className="flex items-center gap-4 mb-4" data-id="element-53">
            <div className={`w-14 h-14 rounded-full ${tutor.color} flex items-center justify-center text-lg font-bold border-4 border-[#FDF8F3]`} data-id="element-54">
              {tutor.initials}
            </div>
            <div data-id="element-55">
              <h3 className="font-bold text-[#3D3428] text-lg" data-id="element-56">{tutor.name}</h3>
              <p className="text-[#8B6914] text-sm" data-id="element-57">{tutor.subject}</p>
            </div>
          </div>

          <div className="flex items-center justify-between bg-[#FDF8F3] p-3 rounded-xl" data-id="element-58">
            <div className="flex flex-col" data-id="element-59">
              <span className="text-xs text-gray-500" data-id="element-60">Hourly Rate</span>
              <span className="font-bold text-[#5D4E37]" data-id="element-61">{tutor.price}/hr</span>
            </div>
            <button className="bg-[#5D4E37] text-white px-5 py-2 rounded-lg text-sm font-bold shadow-md" data-id="element-62">
              Book Session
            </button>
          </div>
        </div>)}
    </div>
  </ScreenWrapper>;
// SCREEN 4: SMART TASK MANAGER
export const TaskManagerScreen = () => <ScreenWrapper activeTab="home" data-id="element-63">
    <div className="pt-14 px-6 pb-6" data-id="element-64">
      <h2 className="text-2xl font-bold text-[#3D3428] mb-6" data-id="element-65">
        Smart Task Manager
      </h2>

      {/* Focus Timer Card */}
      <div className="bg-[#3D3428] rounded-3xl p-6 text-white shadow-xl mb-8 relative overflow-hidden" data-id="element-66">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A962] opacity-10 rounded-full -mr-10 -mt-10 blur-2xl" data-id="element-67"></div>

        <div className="flex justify-between items-start mb-6" data-id="element-68">
          <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm" data-id="element-69">
            <Shield className="w-3 h-3 text-[#C9A962]" data-id="element-70" />
            <span className="text-[10px] font-medium tracking-wide" data-id="element-71">
              DISTRACTIONS BLOCKED
            </span>
          </div>
          <MoreHorizontal className="w-5 h-5 text-white/50" data-id="element-72" />
        </div>

        <div className="text-center mb-6" data-id="element-73">
          <div className="text-6xl font-bold tracking-tighter mb-2 font-mono" data-id="element-74">
            25:00
          </div>
          <p className="text-[#C9A962] text-sm font-medium" data-id="element-75">
            Stay focused, success is near.
          </p>
        </div>

        <button className="w-full bg-[#C9A962] text-white py-3 rounded-xl font-bold shadow-lg shadow-[#C9A962]/20 flex items-center justify-center gap-2" data-id="element-76">
          <Clock className="w-4 h-4" data-id="element-77" />
          Emergency Focus Mode
        </button>
      </div>

      {/* Tasks List */}
      <h3 className="font-bold text-[#3D3428] mb-4 text-lg" data-id="element-78">Today's Tasks</h3>
      <div className="space-y-3" data-id="element-79">
        {[{
        text: 'Finish Math Revision Notes',
        checked: false
      }, {
        text: 'Submit Chemistry Assignment',
        checked: true
      }, {
        text: 'Prepare for GED Exam Practice',
        checked: false
      }].map((task, i) => <div key={i} className={`p-4 rounded-2xl border flex items-center gap-3 transition-all ${task.checked ? 'bg-[#FDF8F3] border-transparent opacity-60' : 'bg-white border-[#F5F0E6] shadow-sm'}`} data-id="element-80">
            <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${task.checked ? 'bg-[#C9A962] border-[#C9A962]' : 'border-gray-300'}`} data-id="element-81">
              {task.checked && <CheckSquare className="w-4 h-4 text-white" data-id="element-82" />}
            </div>
            <span className={`text-sm font-medium ${task.checked ? 'text-gray-400 line-through' : 'text-[#3D3428]'}`} data-id="element-83">
              {task.text}
            </span>
          </div>)}
      </div>
    </div>
  </ScreenWrapper>;
// SCREEN 5: LIVE CLASSES
export const LiveClassesScreen = () => <ScreenWrapper activeTab="home" data-id="element-84">
    <div className="pt-14 px-6 pb-4" data-id="element-85">
      <div className="flex justify-between items-center mb-6" data-id="element-86">
        <h2 className="text-2xl font-bold text-[#3D3428]" data-id="element-87">Live Classes</h2>
        <span className="text-[#C9A962] text-sm font-bold" data-id="element-88">My Schedule</span>
      </div>

      {/* Featured Course */}
      <div className="bg-white rounded-3xl p-1 shadow-sm border border-[#F5F0E6] mb-6" data-id="element-89">
        <div className="bg-gray-100 h-40 rounded-t-[20px] rounded-b-xl relative overflow-hidden" data-id="element-90">
          <div className="absolute inset-0 bg-gradient-to-br from-[#3D3428] to-[#5D4E37]" data-id="element-91"></div>
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-92">
            <Video className="w-12 h-12 text-white/50" data-id="element-93" />
          </div>
          <div className="absolute top-3 left-3 bg-[#C9A962] text-white text-xs font-bold px-2 py-1 rounded-md" data-id="element-94">
            LIVE NOW
          </div>
        </div>
        <div className="p-4" data-id="element-95">
          <h3 className="font-bold text-[#3D3428] text-lg mb-1" data-id="element-96">
            Grade 11 Advanced Math
          </h3>
          <p className="text-gray-500 text-sm mb-4" data-id="element-97">
            Master calculus with expert guidance
          </p>
          <div className="flex items-center justify-between" data-id="element-98">
            <span className="font-bold text-[#5D4E37]" data-id="element-99">
              10 OMR
              <span className="text-xs font-normal text-gray-400" data-id="element-100">/month</span>
            </span>
            <button className="bg-[#3D3428] text-white px-5 py-2 rounded-xl text-sm font-bold" data-id="element-101">
              Subscribe
            </button>
          </div>
        </div>
      </div>

      {/* Other Courses */}
      <div className="space-y-4 overflow-y-auto pb-24" data-id="element-102">
        {[{
        title: 'Physics Exam Crash Program',
        price: '8 OMR',
        color: 'bg-blue-50'
      }, {
        title: 'GED Full Prep Package',
        price: '15 OMR',
        color: 'bg-purple-50'
      }].map((course, i) => <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-[#F5F0E6] flex gap-4 items-center" data-id="element-103">
            <div className={`w-16 h-16 rounded-xl ${course.color} flex-shrink-0 flex items-center justify-center`} data-id="element-104">
              <Video className="w-6 h-6 text-gray-400" data-id="element-105" />
            </div>
            <div className="flex-1" data-id="element-106">
              <h3 className="font-bold text-[#3D3428] text-sm mb-1" data-id="element-107">
                {course.title}
              </h3>
              <p className="text-[#C9A962] font-bold text-sm" data-id="element-108">
                {course.price}
                <span className="text-gray-400 text-xs font-normal" data-id="element-109">
                  /month
                </span>
              </p>
            </div>
            <button className="bg-[#FDF8F3] text-[#5D4E37] px-3 py-1.5 rounded-lg text-xs font-bold border border-[#F5F0E6]" data-id="element-110">
              Subscribe
            </button>
          </div>)}
      </div>
    </div>
  </ScreenWrapper>;
// SCREEN 6: NAJAH AI
export const NajahAIScreen = () => <ScreenWrapper activeTab="home" hideBottomNav={true} data-id="element-111">
    <div className="pt-14 px-6 pb-4 flex flex-col h-full" data-id="element-112">
      <div className="flex items-center justify-center mb-6 relative" data-id="element-113">
        <h2 className="text-lg font-bold text-[#3D3428]" data-id="element-114">Najah AI</h2>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#F5F0E6] flex items-center justify-center" data-id="element-115">
          <MoreHorizontal className="w-4 h-4 text-gray-400" data-id="element-116" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-6 pb-4" data-id="element-117">
        {/* AI Message */}
        <div className="flex gap-3 items-start" data-id="element-118">
          <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#C9A962] to-[#F5E6C8] flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-119">
            <Cpu className="w-5 h-5 text-white" data-id="element-120" />
          </div>
          <div className="bg-white p-4 rounded-2xl rounded-tl-none shadow-sm border border-[#F5F0E6] max-w-[85%]" data-id="element-121">
            <p className="text-[#3D3428] text-sm leading-relaxed" data-id="element-122">
              Hello! I'm Najah AI. I can provide you with notes, quizzes,
              explanations, and study resources anytime you need.
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="pl-12 grid grid-cols-1 gap-2" data-id="element-123">
          <button className="bg-white border border-[#F5F0E6] p-3 rounded-xl text-left text-sm text-[#5D4E37] font-medium hover:bg-[#FDF8F3] transition-colors flex items-center gap-2" data-id="element-124">
            <CheckSquare className="w-4 h-4 text-[#C9A962]" data-id="element-125" /> Practice Quiz
          </button>
          <button className="bg-white border border-[#F5F0E6] p-3 rounded-xl text-left text-sm text-[#5D4E37] font-medium hover:bg-[#FDF8F3] transition-colors flex items-center gap-2" data-id="element-126">
            <BookOpen className="w-4 h-4 text-[#C9A962]" data-id="element-127" /> Explain Concepts
          </button>
          <button className="bg-white border border-[#F5F0E6] p-3 rounded-xl text-left text-sm text-[#5D4E37] font-medium hover:bg-[#FDF8F3] transition-colors flex items-center gap-2" data-id="element-128">
            <Star className="w-4 h-4 text-[#C9A962]" data-id="element-129" /> Study Tips
          </button>
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white p-2 rounded-[20px] shadow-lg border border-[#F5F0E6] flex items-center gap-2 mb-6" data-id="element-130">
        <input type="text" placeholder="Ask me anything..." className="flex-1 bg-transparent border-none px-4 py-3 text-sm focus:outline-none text-[#3D3428]" data-id="element-131" />
        <button className="w-10 h-10 rounded-xl bg-[#C9A962] flex items-center justify-center text-white shadow-md" data-id="element-132">
          <Send className="w-4 h-4" data-id="element-133" />
        </button>
      </div>
    </div>
  </ScreenWrapper>;
// SCREEN 7: CHATS
export const ChatsScreen = () => <ScreenWrapper activeTab="chats" data-id="element-134">
    <div className="pt-14 px-6 pb-2" data-id="element-135">
      <div className="flex justify-between items-center mb-6" data-id="element-136">
        <h2 className="text-2xl font-bold text-[#3D3428]" data-id="element-137">Chats</h2>
        <div className="w-8 h-8 rounded-full bg-white border border-[#F5F0E6] flex items-center justify-center" data-id="element-138">
          <MessageSquare className="w-4 h-4 text-[#C9A962]" data-id="element-139" />
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6" data-id="element-140">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-141" />
        <input type="text" placeholder="Search messages..." className="w-full bg-white border border-[#F5F0E6] rounded-xl py-3 pl-10 pr-4 text-sm text-[#3D3428] focus:outline-none" data-id="element-142" />
      </div>
    </div>

    {/* Chat List */}
    <div className="px-6 space-y-1 overflow-y-auto pb-24" data-id="element-143">
      {[{
      name: 'Ahmed Saidi',
      message: 'Are your Algebra notes still available?',
      time: '10:30 AM',
      unread: 2,
      color: 'bg-blue-100'
    }, {
      name: 'Sarah Al-Maawali',
      message: 'Can you send the Calculus summary?',
      time: 'Yesterday',
      unread: 0,
      color: 'bg-pink-100'
    }, {
      name: 'Tutor Aisha',
      message: 'Your session is confirmed for tomorrow.',
      time: 'Yesterday',
      unread: 0,
      color: 'bg-purple-100'
    }].map((chat, i) => <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-[#F5F0E6] hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-144">
          <div className={`w-12 h-12 rounded-full ${chat.color} flex items-center justify-center font-bold text-[#5D4E37] text-sm`} data-id="element-145">
            {chat.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
          </div>
          <div className="flex-1 min-w-0" data-id="element-146">
            <div className="flex justify-between items-center mb-1" data-id="element-147">
              <h3 className="font-bold text-[#3D3428] text-sm truncate" data-id="element-148">
                {chat.name}
              </h3>
              <span className="text-[10px] text-gray-400" data-id="element-149">{chat.time}</span>
            </div>
            <p className={`text-xs truncate ${chat.unread > 0 ? 'text-[#3D3428] font-semibold' : 'text-gray-500'}`} data-id="element-150">
              {chat.message}
            </p>
          </div>
          {chat.unread > 0 && <div className="w-5 h-5 rounded-full bg-[#C9A962] flex items-center justify-center text-[10px] text-white font-bold" data-id="element-151">
              {chat.unread}
            </div>}
        </div>)}
    </div>

    {/* Input Field (Simulated at bottom) */}
    <div className="absolute bottom-20 left-6 right-6 bg-white p-2 rounded-xl border border-[#F5F0E6] shadow-lg flex items-center gap-2 z-10" data-id="element-152">
      <input type="text" placeholder="Type a message..." className="flex-1 bg-transparent border-none px-3 py-2 text-xs focus:outline-none" disabled data-id="element-153" />
      <div className="w-8 h-8 rounded-lg bg-[#FDF8F3] flex items-center justify-center text-gray-400" data-id="element-154">
        <Send className="w-3 h-3" data-id="element-155" />
      </div>
    </div>
  </ScreenWrapper>;